/**
 * The kitchen buddies.
 *
 * Drawn, never emoji (rule 13): an emoji tomato is a different tomato on every
 * phone, cannot animate, and a screen reader reads it as "tomato" rather than
 * as a friend cheering you on. Same construction as Adulting's Pichi — one
 * 120×120 viewBox, one ink outline, a shared face — so five characters stay
 * one family.
 *
 * Moods are never sad. The worst a buddy gets is thoughtful.
 */
import type { BuddyKey } from '../../../shared/kitchen'

export type Mood = 'happy' | 'cheer' | 'think' | 'wow'

const INK = '#2A1420'
const CHEEK = '#FF8FA3'

interface Shape {
  /** Where the face is centred. */
  face: [number, number]
  /** Wears sunglasses instead of showing eyes — in every mood. */
  shades?: boolean
  /** Top of the head, where the hat sits. */
  top: number
  /** Shoulder points for the arms. */
  arms: [[number, number], [number, number]]
  /** Feet x positions and ground y. */
  feet: [number, number, number]
  body: React.ReactNode
}

const SHAPES: Record<BuddyKey, Shape> = {
  tomate: {
    face: [60, 70], top: 32, arms: [[27, 74], [93, 74]], feet: [48, 72, 104],
    body: (
      <>
        <circle cx="60" cy="70" r="34" fill="#F0503F" stroke={INK} strokeWidth="3" />
        <ellipse cx="45" cy="54" rx="9" ry="5" fill="#fff" opacity=".35" transform="rotate(-35 45 54)" />
        <g fill="#5DBB4C" stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
          <path d="M60 40c-6-6-15-6-20-2 6 1 11 4 13 8z" />
          <path d="M60 40c6-6 15-6 20-2-6 1-11 4-13 8z" />
          <path d="M60 41c-3-7-1-12 3-15 1 5 1 10-1 15z" />
        </g>
        <path d="M61 27c0-4 2-7 5-8" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      </>
    ),
  },
  aguacate: {
    face: [60, 54], top: 20, arms: [[31, 70], [89, 70]], feet: [48, 72, 106],
    body: (
      <>
        <path d="M60 18c18 0 26 26 30 44 6 26-10 44-30 44S24 88 30 62c4-18 12-44 30-44z" fill="#3E7B27" stroke={INK} strokeWidth="3" />
        <path d="M60 28c14 0 20 20 23 36 5 22-7 34-23 34S32 86 37 64c3-16 9-36 23-36z" fill="#CBE88C" />
        <circle cx="60" cy="86" r="10" fill="#9A5B2E" stroke={INK} strokeWidth="2.5" />
        <ellipse cx="56.5" cy="82.5" rx="3.2" ry="2.5" fill="#fff" opacity=".35" />
      </>
    ),
  },
  bao: {
    face: [60, 74], top: 34, arms: [[22, 84], [98, 84]], feet: [46, 74, 100],
    body: (
      <>
        <path d="M16 96c0-34 20-56 44-56s44 22 44 56c0 4-4 6-8 6H24c-4 0-8-2-8-6z" fill="#FFF3DE" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <g fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" opacity=".55">
          <path d="M60 41c-6 4-12 4-16 1" /><path d="M60 41c6 4 12 4 16 1" /><path d="M60 41v-1" />
        </g>
        <path d="M54 42c2-7 10-7 12 0" fill="#FFE7C2" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <ellipse cx="40" cy="62" rx="8" ry="5" fill="#fff" opacity=".6" transform="rotate(-30 40 62)" />
      </>
    ),
  },
  flan: {
    face: [60, 80], top: 46, arms: [[33, 80], [87, 80]], feet: [48, 72, 104],
    body: (
      <>
        <ellipse cx="60" cy="101" rx="42" ry="6" fill="#fff" stroke={INK} strokeWidth="2.5" />
        <path d="M28 98l8-44c8-6 40-6 48 0l8 44c-14 5-50 5-64 0z" fill="#FFCF6E" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <path d="M36 54c8-6 40-6 48 0l-1.5 9c-3 4-7 4-9-1-3 6-9 6-12 0-3 6-9 6-12 0-2 5-7 5-10 1z" fill="#A4551F" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <ellipse cx="50" cy="55" rx="6" ry="2" fill="#fff" opacity=".35" />
      </>
    ),
  },
  platano: {
    face: [56, 66], top: 22, arms: [[33, 72], [79, 70]], feet: [48, 70, 106],
    body: (
      <>
        <path d="M46 22c-16 14-20 54-2 76 10 10 32 10 42 0-18-10-26-38-22-70 1-8-10-12-18-6z" fill="#FFD84A" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <path d="M58 26c-4 22-2 50 14 70" fill="none" stroke="#E2B32A" strokeWidth="3" strokeLinecap="round" />
        <path d="M46 22l-3-7c2-2 6-2 8 0l1 5" fill="#6B4A2B" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M84 99c3 2 6 1 7-1" fill="none" stroke="#6B4A2B" strokeWidth="4" strokeLinecap="round" />
      </>
    ),
  },
  picante: {
    face: [54, 60], top: 24, shades: true, arms: [[33, 70], [77, 68]], feet: [50, 72, 106],
    body: (
      <>
        <path d="M40 34c-14 12-14 50 8 66 12 9 34 8 44-2-20-6-28-32-22-58 2-10-18-14-30-6z" fill="#E8352B" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <path d="M39 44c-4 14-3 30 4 42" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" opacity=".35" />
        <path d="M38 35c6-7 26-8 33-1-4 6-28 7-33 1z" fill="#4FA83D" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M55 31c0-8 4-13 11-15" fill="none" stroke={INK} strokeWidth="5" strokeLinecap="round" />
        <path d="M55 31c0-8 4-13 11-15" fill="none" stroke="#4FA83D" strokeWidth="2.5" strokeLinecap="round" />
      </>
    ),
  },
}

/** Spicy's shades: two dark lenses, a bridge, and a glint so they read as glass. */
function Shades({ at: [cx, cy] }: { at: [number, number] }) {
  return (
    <g stroke={INK} strokeWidth="2.2" strokeLinejoin="round">
      <path d={`M${cx - 21} ${cy - 3}h4M${cx + 17} ${cy - 3}h4`} strokeLinecap="round" />
      <path d={`M${cx - 18} ${cy - 5}h14c0 6-2 10-7 10s-7-4-7-10z`} fill="#1A1016" />
      <path d={`M${cx + 4} ${cy - 5}h14c0 6-2 10-7 10s-7-4-7-10z`} fill="#1A1016" />
      <path d={`M${cx - 4} ${cy - 4}q4-3 8 0`} fill="none" />
      <path d={`M${cx - 15} ${cy - 2}l4 0M${cx + 7} ${cy - 2}l4 0`} stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity=".8" />
    </g>
  )
}

function Face({ at: [cx, cy], mood, shades }: { at: [number, number]; mood: Mood; shades?: boolean }) {
  const eyeDx = 10
  return (
    <g>
      {shades ? <Shades at={[cx, cy]} /> : mood === 'cheer' ? (
        <g fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round">
          <path d={`M${cx - eyeDx - 4} ${cy + 1}q4-6 8 0`} />
          <path d={`M${cx + eyeDx - 4} ${cy + 1}q4-6 8 0`} />
        </g>
      ) : (
        <g fill={INK}>
          <ellipse cx={cx - eyeDx} cy={cy} rx={mood === 'wow' ? 4.6 : 3.6} ry={mood === 'wow' ? 5.6 : 4.6} />
          <ellipse cx={cx + eyeDx} cy={cy} rx={mood === 'wow' ? 4.6 : 3.6} ry={mood === 'wow' ? 5.6 : 4.6} />
          {/* The glint is what makes them look alive; looking up when thinking. */}
          <circle cx={cx - eyeDx + 1.2} cy={cy - (mood === 'think' ? 2.6 : 1.4)} r="1.4" fill="#fff" />
          <circle cx={cx + eyeDx + 1.2} cy={cy - (mood === 'think' ? 2.6 : 1.4)} r="1.4" fill="#fff" />
        </g>
      )}
      <ellipse cx={cx - eyeDx - 5} cy={cy + 8} rx="4.5" ry="2.8" fill={CHEEK} opacity=".8" />
      <ellipse cx={cx + eyeDx + 5} cy={cy + 8} rx="4.5" ry="2.8" fill={CHEEK} opacity=".8" />
      {mood === 'happy' && <path d={`M${cx - 5} ${cy + 7}q5 5 10 0`} fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />}
      {mood === 'cheer' && <path d={`M${cx - 6} ${cy + 6}h12q-1 8-6 8t-6-8z`} fill="#7A1F2B" stroke={INK} strokeWidth="2.2" strokeLinejoin="round" />}
      {mood === 'wow' && <ellipse cx={cx} cy={cy + 10} rx="3.4" ry="4.2" fill="#7A1F2B" stroke={INK} strokeWidth="2.2" />}
      {mood === 'think' && <path d={`M${cx - 4} ${cy + 10}l8-1.5`} fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />}
    </g>
  )
}

function Hat({ top, size }: { top: number; size: 1 | 2 }) {
  // A toque: a band on the head and a puff above it that grows with the level.
  const b = top - 2
  const h = size === 2 ? 26 : 14
  return (
    <g className="buddy-hat" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" fill="#fff">
      <path d={`M47 ${b}C37 ${b} 38 ${b - h + 3} 47 ${b - h + 6}C48 ${b - h - 4} 72 ${b - h - 4} 73 ${b - h + 6}C82 ${b - h + 3} 83 ${b} 73 ${b}z`} />
      <rect x="47" y={b - 1} width="26" height="7" rx="2" />
      {size === 2 && <path d={`M55 ${b - 4}v-${h - 10}M65 ${b - 4}v-${h - 10}`} fill="none" opacity=".3" />}
    </g>
  )
}

export default function Buddy({ kind, mood = 'happy', hat = 0, size = 120, className, title }: {
  kind: BuddyKey
  mood?: Mood
  hat?: 0 | 1 | 2
  size?: number
  className?: string
  /** Set when the buddy is the only thing carrying the meaning. */
  title?: string
}) {
  const s = SHAPES[kind]
  const [[lx, ly], [rx, ry]] = s.arms
  const up = mood === 'cheer'
  const [fl, fr, ground] = s.feet
  return (
    <svg
      className={['buddy', `buddy-${mood}`, className].filter(Boolean).join(' ')}
      width={size} height={size} viewBox="-8 -14 136 136"
      role={title ? 'img' : undefined} aria-hidden={title ? undefined : true} focusable="false"
    >
      {title && <title>{title}</title>}
      <ellipse cx="60" cy="112" rx="30" ry="4" fill={INK} opacity=".12" />
      <g className="buddy-body">
        <g fill="#2A1420">
          <ellipse cx={fl} cy={ground + 2} rx="7" ry="4" />
          <ellipse cx={fr} cy={ground + 2} rx="7" ry="4" />
        </g>
        <g fill="none" stroke={INK} strokeWidth="5" strokeLinecap="round">
          <path className="buddy-arm-l" d={up ? `M${lx} ${ly}l-10-14` : `M${lx} ${ly}l-9 8`} />
          <path className="buddy-arm-r" d={up ? `M${rx} ${ry}l10-14` : `M${rx} ${ry}l9 8`} />
        </g>
        {s.body}
        <Face at={s.face} mood={mood} shades={s.shades} />
        {hat > 0 && <Hat top={s.top} size={hat as 1 | 2} />}
      </g>
      {mood === 'cheer' && (
        <g fill="#FFC23D" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" className="buddy-sparkle">
          <path d="M16 26l2.5 5.5 5.5 2.5-5.5 2.5L16 42l-2.5-5.5L8 34l5.5-2.5z" />
          <path d="M104 18l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" />
        </g>
      )}
      {mood === 'think' && (
        <g fill="#fff" stroke={INK} strokeWidth="2">
          <circle cx="98" cy="30" r="9" />
          <circle cx="88" cy="44" r="3" />
          <g fill={INK} stroke="none"><circle cx="94" cy="30" r="1.6" /><circle cx="98" cy="30" r="1.6" /><circle cx="102" cy="30" r="1.6" /></g>
        </g>
      )}
    </svg>
  )
}
